import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { callFunction } from '@/lib/api';
import { patientDetailQueryKey } from '@/hooks/usePatients';

export interface PatientPortalInvitation {
  id: string;
  expires_at: string;
  created_at: string;
}

export const patientPortalInvitationKey = (patientId: string) =>
  ['patient-portal', 'invitation', patientId] as const;

export function usePatientPortalInvitation(patientId: string | undefined) {
  return useQuery({
    queryKey: patientPortalInvitationKey(patientId ?? ''),
    enabled: Boolean(patientId),
    queryFn: async (): Promise<PatientPortalInvitation | null> => {
      if (!patientId) return null;

      const sb = supabase as unknown as {
        from: (table: string) => {
          select: (cols: string) => {
            eq: (col: string, val: string) => {
              is: (col: string, val: null) => {
                gt: (col: string, val: string) => {
                  maybeSingle: () => Promise<{
                    data: PatientPortalInvitation | null;
                    error: { message: string } | null;
                  }>;
                };
              };
            };
          };
        };
      };

      const { data, error } = await sb
        .from('patient_portal_invitations')
        .select('id, expires_at, created_at')
        .eq('patient_id', patientId)
        .is('accepted_at', null)
        .gt('expires_at', new Date().toISOString())
        .maybeSingle();

      if (error) throw error;
      return data as PatientPortalInvitation | null;
    },
    staleTime: 10_000,
  });
}

interface InvitePatientPortalOutput {
  status: 'invited' | 'linked';
  email: string;
  invitation_id?: string;
  expires_at?: string;
}

export function useGrantPatientPortalAccess(patientId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () =>
      callFunction<{ patient_id: string }, InvitePatientPortalOutput>(
        'invite-patient-portal',
        { patient_id: patientId },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: patientDetailQueryKey(patientId) });
      qc.invalidateQueries({ queryKey: patientPortalInvitationKey(patientId) });
    },
  });
}

export function useRevokePatientPortalAccess(patientId: string) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: () =>
      callFunction<{ patient_id: string }, { patient_id: string; revoked: true }>(
        'revoke-patient-portal',
        { patient_id: patientId },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: patientDetailQueryKey(patientId) });
      qc.invalidateQueries({ queryKey: patientPortalInvitationKey(patientId) });
    },
  });
}
